/**
 * Типизированный fetch wrapper для PCP API.
 *
 * Использует относительные URL — Vite proxy в dev перенаправляет /v1 → :3000,
 * в production frontend и backend обслуживаются с одного домена через Traefik.
 */
import type {
  CompanyCard,
  BulkCheckResponse,
  RiskExplain,
  SuggestResponse,
  Paginated,
  CompareResponse,
  RelatedResponse,
  FacetsResponse,
  RegistriesResponse,
  AdvancedSearchRequest,
  MonitoringSubscription,
  MonitoringAlert,
  IpEntity,
  PersonCard,
  DirectorsResponse,
  FoundersResponse,
  TaxRegimeResponse,
  VerdictResponse,
  EnforcementResult,
  EnforcementCheckResponse,
} from '../data/types';

interface FetchOptions {
  signal?: AbortSignal;
  /** JWT access token для приватных endpoints. */
  token?: string;
}

class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function pcpFetch<T>(path: string, init: RequestInit & FetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
  };
  if (init.token) headers.Authorization = `Bearer ${init.token}`;

  const res = await fetch(path, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });

  if (!res.ok) {
    // Читаем тело ОДИН раз — text(), парсим как JSON если получится
    let body: unknown;
    try {
      const txt = await res.text();
      try {
        body = JSON.parse(txt);
      } catch {
        body = txt;
      }
    } catch {
      body = null;
    }
    throw new ApiError(res.status, `HTTP ${res.status}`, body);
  }
  return res.json() as Promise<T>;
}

export const api = {
  /** GET /v1/companies/{slug} */
  company(slug: string, opts?: FetchOptions): Promise<CompanyCard> {
    return pcpFetch<CompanyCard>(`/v1/companies/${encodeURIComponent(slug)}`, opts ?? {});
  },

  /** GET /v1/companies/{slug}/risk/explain */
  riskExplain(slug: string, opts?: FetchOptions): Promise<RiskExplain> {
    return pcpFetch<RiskExplain>(
      `/v1/companies/${encodeURIComponent(slug)}/risk/explain`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/{slug}/verdict — вердикт сделки (работать/осторожно/отказ). */
  verdict(slug: string, opts?: FetchOptions): Promise<VerdictResponse> {
    return pcpFetch<VerdictResponse>(
      `/v1/companies/${encodeURIComponent(slug)}/verdict`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/{slug}/financials (ГИРБО lazy) */
  financials(slug: string, opts?: FetchOptions): Promise<{ items: unknown[]; source: string }> {
    return pcpFetch(
      `/v1/companies/${encodeURIComponent(slug)}/financials`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/suggest?q=&limit= */
  suggest(q: string, limit = 10, opts?: FetchOptions): Promise<SuggestResponse> {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return pcpFetch<SuggestResponse>(`/v1/companies/suggest?${params}`, opts ?? {});
  },

  /** POST /v1/companies/bulk */
  bulk(inns: string[], opts?: FetchOptions): Promise<BulkCheckResponse> {
    return pcpFetch<BulkCheckResponse>('/v1/companies/bulk', {
      ...opts,
      method: 'POST',
      body: JSON.stringify({ inns }),
    });
  },

  /** GET /v1/analytics/dashboard */
  dashboard(opts?: FetchOptions): Promise<unknown> {
    return pcpFetch('/v1/analytics/dashboard', opts ?? {});
  },

  /** POST /v1/companies/search */
  search(
    body: { q: string; page?: number; per_page?: number; region?: string[] },
    opts?: FetchOptions
  ): Promise<Paginated<unknown>> {
    return pcpFetch<Paginated<unknown>>('/v1/companies/search', {
      ...opts,
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** POST /v1/companies/compare — сравнение 2-5 ИНН side-by-side. */
  compare(inns: string[], opts?: FetchOptions): Promise<CompareResponse> {
    return pcpFetch<CompareResponse>('/v1/companies/compare', {
      ...opts,
      method: 'POST',
      body: JSON.stringify({ inns }),
    });
  },

  /** GET /v1/companies/{slug}/related — связанные компании (по адресу/директору/peers). */
  related(slug: string, limit = 10, opts?: FetchOptions): Promise<RelatedResponse> {
    return pcpFetch<RelatedResponse>(
      `/v1/companies/${encodeURIComponent(slug)}/related?limit=${limit}`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/facets — фасет-фильтры для search-сайдбара. */
  facets(q?: string, opts?: FetchOptions): Promise<FacetsResponse> {
    const url = q ? `/v1/companies/facets?q=${encodeURIComponent(q)}` : '/v1/companies/facets';
    return pcpFetch<FacetsResponse>(url, opts ?? {});
  },

  /** GET /v1/companies/{slug}/registries — Минцифры ПО + сертификаты + СРО членство. */
  registries(slug: string, opts?: FetchOptions): Promise<RegistriesResponse> {
    return pcpFetch<RegistriesResponse>(
      `/v1/companies/${encodeURIComponent(slug)}/registries`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/{slug}/enforcement — исполнительные производства ФССП. */
  enforcement(slug: string, opts?: FetchOptions): Promise<EnforcementResult> {
    return pcpFetch<EnforcementResult>(
      `/v1/companies/${encodeURIComponent(slug)}/enforcement`,
      opts ?? {}
    );
  },

  /** GET /v1/enforcement/check?inn= — публичная проверка по ИНН (ЮЛ / редирект для физлиц). */
  enforcementCheck(inn: string, opts?: FetchOptions): Promise<EnforcementCheckResponse> {
    return pcpFetch<EnforcementCheckResponse>(
      `/v1/enforcement/check?inn=${encodeURIComponent(inn)}`,
      opts ?? {}
    );
  },

  /** POST /v1/companies/search-advanced — search с реестр-фильтрами. */
  searchAdvanced(
    body: AdvancedSearchRequest,
    opts?: FetchOptions
  ): Promise<Paginated<unknown> & { filters_applied: Record<string, unknown> }> {
    return pcpFetch('/v1/companies/search-advanced', {
      ...opts,
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** PDF report — возвращает Blob для скачивания / iframe preview. */
  reportPdfUrl(slug: string): string {
    return `/v1/companies/${encodeURIComponent(slug)}/report.pdf`;
  },

  /** GET /v1/ip/{slug} — карточка ИП. */
  ip(slug: string, opts?: FetchOptions): Promise<IpEntity> {
    return pcpFetch<IpEntity>(`/v1/ip/${encodeURIComponent(slug)}`, opts ?? {});
  },

  /** GET /v1/persons/{slug} — карточка физлица. */
  person(slug: string, opts?: FetchOptions): Promise<PersonCard> {
    return pcpFetch<PersonCard>(`/v1/persons/${encodeURIComponent(slug)}`, opts ?? {});
  },

  /** GET /v1/companies/{slug}/directors — текущий + история руководителей. */
  directors(slug: string, opts?: FetchOptions): Promise<DirectorsResponse> {
    return pcpFetch<DirectorsResponse>(
      `/v1/companies/${encodeURIComponent(slug)}/directors`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/{slug}/founders — учредители. */
  founders(slug: string, opts?: FetchOptions): Promise<FoundersResponse> {
    return pcpFetch<FoundersResponse>(
      `/v1/companies/${encodeURIComponent(slug)}/founders`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/{slug}/tax-regime — налоговый режим (УСН/ЕСХН/АУСН/ОСНО). */
  taxRegime(slug: string, opts?: FetchOptions): Promise<TaxRegimeResponse> {
    return pcpFetch<TaxRegimeResponse>(
      `/v1/companies/${encodeURIComponent(slug)}/tax-regime`,
      opts ?? {}
    );
  },

  /** GET /v1/companies/top — топ N компаний по выручке. */
  top(limit = 20, opts?: FetchOptions): Promise<{ items: unknown[]; total: number }> {
    return pcpFetch(`/v1/companies/top?limit=${limit}`, opts ?? {});
  },

  /** GET /v1/companies/recent — последние добавленные. */
  recent(limit = 12, opts?: FetchOptions): Promise<{ items: unknown[] }> {
    return pcpFetch(`/v1/companies/recent?limit=${limit}`, opts ?? {});
  },

  /** GET /v1/monitoring (требует auth). */
  monitoringList(opts?: FetchOptions): Promise<{ items: MonitoringSubscription[]; total: number }> {
    return pcpFetch('/v1/monitoring', opts ?? {});
  },

  /** POST /v1/monitoring (требует auth). */
  monitoringCreate(
    body: { entity_type: 'company' | 'person' | 'ip'; entity_slug: string; watch_finance?: boolean; channels?: string[] },
    opts?: FetchOptions
  ): Promise<MonitoringSubscription> {
    return pcpFetch<MonitoringSubscription>('/v1/monitoring', {
      ...opts,
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** DELETE /v1/monitoring/{id} (требует auth). */
  monitoringDelete(id: string, opts?: FetchOptions): Promise<{ id: string; deleted: boolean }> {
    return pcpFetch(`/v1/monitoring/${id}`, { ...opts, method: 'DELETE' });
  },

  /** GET /v1/monitoring/alerts (требует auth). */
  monitoringAlerts(unread = false, opts?: FetchOptions): Promise<{ items: MonitoringAlert[]; total: number }> {
    return pcpFetch(`/v1/monitoring/alerts${unread ? '?unread=true' : ''}`, opts ?? {});
  },
};

export { ApiError };
